import { ActionIcon, Box, Collapse, Group, Skeleton, Text, Tooltip } from '@mantine/core';
import { ArrowsOut, ArrowsIn, ArrowClockwise, Sparkle } from '@phosphor-icons/react';
import React, { memo } from 'react';

import type { SalesInsightItem } from './types';
import classes from './SalesInsightsCard.module.css';

type SalesInsightsCardProps = {
    insights: SalesInsightItem[];
    loading: boolean;
    collapsed: boolean;
    onToggleCollapse: () => void;
    onRefresh: () => void;
};

const sentimentClass: Record<string, string> = {
    positive: classes.cardPositive,
    negative: classes.cardNegative,
    neutral: classes.cardNeutral,
};

const InsightCard: React.FC<{ item: SalesInsightItem }> = memo(({ item }) => (
    <div className={`${classes.card} ${sentimentClass[item.sentiment] ?? classes.cardNeutral}`}>
        <div className={classes.cardTitle}>{item.title}</div>
        <div className={classes.cardBody}>{item.body}</div>
        {item.metric && <div className={classes.cardMetric}>{item.metric}</div>}
    </div>
));
InsightCard.displayName = 'InsightCard';

const SkeletonCards: React.FC = () => (
    <div className={classes.grid}>
        {[1, 2, 3].map((i) => (
            <div key={i} className={classes.skeletonCard}>
                <Skeleton height={14} width="70%" radius={2} />
                <Skeleton height={12} width="100%" radius={2} />
                <Skeleton height={12} width="40%" radius={2} />
            </div>
        ))}
    </div>
);

const SalesInsightsCard: React.FC<SalesInsightsCardProps> = memo(
    ({ insights, loading, collapsed, onToggleCollapse, onRefresh }) => {
        const CollapseIcon = collapsed ? ArrowsOut : ArrowsIn;

        return (
            <Box className={classes.container}>
                <Group className={classes.header}>
                    <Sparkle
                        size={14}
                        weight="fill"
                        style={{
                            color: 'rgb(59, 130, 246)',
                            filter: 'drop-shadow(0 0 2px rgba(59, 130, 246, 0.3))',
                        }}
                    />
                    <Text
                        style={{
                            fontSize: 18,
                            fontWeight: 600,
                            lineHeight: 1.3,
                            color: 'rgb(26, 32, 44)',
                        }}
                    >
                        AI Sales Insights
                    </Text>

                    <div className={classes.headerActions}>
                        <Tooltip label="Refresh insights">
                            <ActionIcon
                                variant="subtle"
                                size="sm"
                                onClick={onRefresh}
                                loading={loading}
                            >
                                <ArrowClockwise size={16} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label={collapsed ? 'Expand' : 'Collapse'}>
                            <ActionIcon
                                variant="subtle"
                                size="sm"
                                onClick={onToggleCollapse}
                            >
                                <CollapseIcon size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </div>
                </Group>

                <Collapse in={!collapsed}>
                    <Box className={classes.spotlightBox}>
                        {loading ? (
                            <SkeletonCards />
                        ) : insights.length > 0 ? (
                            <div className={classes.grid}>
                                {insights.map((item, idx) => (
                                    <InsightCard key={idx} item={item} />
                                ))}
                            </div>
                        ) : (
                            <Text
                                style={{
                                    fontSize: 13,
                                    color: 'rgb(100, 116, 139)',
                                    textAlign: 'center',
                                    padding: 16,
                                }}
                            >
                                No insights available for the selected period.
                            </Text>
                        )}
                    </Box>
                </Collapse>
            </Box>
        );
    },
);

SalesInsightsCard.displayName = 'SalesInsightsCard';

export default SalesInsightsCard;
